import React, { useMemo, useCallback, useRef } from 'react';
import { Message, Box } from 'fbm-ui';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { log } from '@avocadoui/utils';
import { SxProps } from '@mui/material';

type PusherProps = {
  liveUrl?: string;
  onPusherError?: (err: any) => void;
  sx?: SxProps;
  orientation?: 'portrait' | 'landscape';
};

const Pusher = forwardRef(
  (
    { liveUrl = '', onPusherError, sx, orientation = 'landscape' }: PusherProps,
    ref
  ) => {
    const selectedCameraId = useAppSelector(
      (state) => state.room.selectedCameraId
    );
    const selectedMicId = useAppSelector((state) => state.room.selectedMicId);

    const videoStreamIdRef = useRef(null);
    const audioStreamIdRef = useRef(null);
    // 创建推流实例
    // @ts-ignore
    const pushClient = useMemo(() => new TXLivePusher(), []);

    const handlePusherError = useCallback(
      (err) => {
        const { errorCode, message } = err;
        log.capture('Pusher_Error', {
          code: errorCode,
          message: message,
        });
        if ([10000, 10002, 10004, 10008, 10006].includes(errorCode)) {
          Message.error('测试过程中必须开启摄像头');
          onPusherError?.(err);
        } else {
          Message.error(message);
        }
        console.log(err);
      },
      [onPusherError]
    );
    // 监听错误事件
    pushClient.setObserver({
      // 推流连接状态
      onPushStatusUpdate: function (status, message) {
        console.log(status, message);
        log.capture('Pusher_Status', { status, message });
      },
      // 推流统计数据
      onStatisticsUpdate: function () {
        // console.log('video fps is ' + data.video.framesPerSecond);
      },
      onWarning: function (code, message, data) {
        const WARNING_CODE = {
          CAMERA_INTERRUPTED: -1005, // 摄像头中断事件
          MICROPHONE_INTERRUPTED: -1006, // 麦克风中断事件
        };
        const deviceManager = pushClient.getDeviceManager();
        log.capture('Pusher_Warning', { code, message, data });

        if (code === WARNING_CODE.CAMERA_INTERRUPTED) {
          // 摄像头中断，查找剩余可用摄像头
          deviceManager.getDevicesList('video').then(function (devices) {
            if (devices.length > 0 && devices[0].deviceId) {
              // 切换摄像头设备
              deviceManager.switchCamera(devices[0].deviceId, data.streamId);
              log.capture('Pusher_Camera_Error', {
                message: '摄像头采集异常中断，自动切换',
              });
            } else {
              // 结束推流
              if (pushClient.isPushing()) {
                pushClient.stopPush();
                log.capture('Pusher_Camera_Error', {
                  message: '摄像头采集异常中断，推流结束',
                });
              }
              // 停止采集
              pushClient.stopCamera();
              log.capture('Pusher_Camera_Error', {
                message: '停止采集摄像头',
              });
            }
          });
        } else if (code === WARNING_CODE.MICROPHONE_INTERRUPTED) {
          // 麦克风中断，查找剩余可用麦克风
          deviceManager.getDevicesList('audio').then(function (devices) {
            if (devices.length > 0 && devices[0].deviceId) {
              // 切换麦克风设备
              deviceManager.switchMicrophone(
                devices[0].deviceId,
                data.streamId
              );
              log.capture('Pusher_Mic_Error', {
                message: '麦克风采集异常中断，自动切换',
              });
            } else {
              // 结束推流
              if (pushClient.isPushing()) {
                pushClient.stopPush();
                log.capture('Pusher_Mic_Error', {
                  message: '麦克风采集异常中断，推流结束',
                });
              }
              // 停止采集
              pushClient.stopMicrophone();
              log.capture('Pusher_Mic_Error', {
                message: '停止采集麦克风',
              });
            }
          });
        }
      },
    });

    useEffect(() => {
      if (!liveUrl) return;

      pushClient.setRenderView('videoContainer') as HTMLVideoElement;

      pushClient.videoView.muted = true;

      // 设置视频质量
      pushClient.setVideoQuality('720p');
      // 设置音频质量
      pushClient.setAudioQuality('standard');
      // 自定义设置帧率
      pushClient.setProperty('setVideoFPS', 20);
      pushClient.setProperty('setVideoBitrate', 800);
      // 启用回声消除
      pushClient.setProperty('enableAudioAEC', true);
      // 启用噪声抑制
      pushClient.setProperty('enableAudioANS', true);
      if (orientation === 'portrait') {
        pushClient.setProperty('setVideoResolution', {
          width: 480,
          height: 848,
        });
      } else {
        pushClient.setProperty('setVideoResolution', {
          width: 1280,
          height: 720,
        });
      }

      Promise.all([
        pushClient.startCamera(selectedCameraId),
        pushClient.startMicrophone(selectedMicId),
      ])
        .then(([videoStreamId, audioStreamId]) => {
          videoStreamIdRef.current = videoStreamId;
          audioStreamIdRef.current = audioStreamId;
          console.log(videoStreamId, audioStreamId);
        })
        .catch(handlePusherError);

      return () => {
        log.capture('Pusher_Stop', { message: '停止推流' });
        pushClient.stopMicrophone();
        pushClient.stopCamera();
        pushClient.stopPush();
      };
    }, [
      handlePusherError,
      liveUrl,
      orientation,
      pushClient,
      selectedCameraId,
      selectedMicId,
    ]);

    useEffect(() => {
      return () => {
        console.log('puhser destroy');
        pushClient?.destroy();
      };
    }, [pushClient]);

    useImperativeHandle(ref, () => ({
      stopPush: () => {
        log.capture('Pusher_Stop', { message: '停止推流' });
        pushClient.stopMicrophone();
        pushClient.stopCamera();
        pushClient.stopPush();
      },
      startPush: () => {
        log.capture('Pusher_Start', { message: '开始推流' });
        pushClient
          .startPush(liveUrl)
          .then(console.log)
          .catch(handlePusherError);
      },
      toggleCamera: () => {
        log.capture('Pusher_ToggleCamera', { message: '切换摄像头' });
        if (videoStreamIdRef.current) {
          videoStreamIdRef.current = null;
          pushClient.stopCamera();
        } else {
          pushClient
            .startCamera()
            .then((videoStreamId) => {
              videoStreamIdRef.current = videoStreamId;
            })
            .catch(handlePusherError);
        }
      },
      toggleMic: () => {
        log.capture('Pusher_ToggleMic', { message: '切换麦克风' });
        if (audioStreamIdRef.current) {
          audioStreamIdRef.current = null;
          pushClient.stopMicrophone();
        } else {
          pushClient
            .startMicrophone()
            .then((audioStreamId) => {
              audioStreamIdRef.current = audioStreamId;
            })
            .catch(handlePusherError);
        }
      },
      getDevicesList: () => {
        const deviceManager = pushClient.getDeviceManager();
        return deviceManager.getDevicesList();
      },
    }));

    useEffect(() => {
      if (selectedCameraId && selectedCameraId !== 'default') {
        const deviceManager = pushClient.getDeviceManager();
        deviceManager.switchCamera(selectedCameraId);
      }
    }, [selectedCameraId, pushClient]);

    useEffect(() => {
      if (selectedMicId && selectedMicId !== 'default') {
        const deviceManager = pushClient.getDeviceManager();
        deviceManager.switchMicrophone(selectedMicId);
      }
    }, [selectedMicId, pushClient]);

    return (
      <Box
        id="videoContainer"
        sx={{ width: '100%', height: '100%', ...sx }}
      ></Box>
    );
  }
);

export default React.memo(Pusher);
